import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { monitoringAPI, employeeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Monitor, 
  MapPin, 
  Activity, 
  AlertTriangle, 
  Shield,
  Clock,
  Eye,
  Globe,
  Wifi,
  WifiOff,
  Play,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const Monitoring = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [vpnAlerts, setVpnAlerts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchLocations();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [employeesRes, locationsRes, logsRes, vpnRes] = await Promise.all([
        employeeAPI.getAll(),
        monitoringAPI.getAllLocations(),
        monitoringAPI.getLoginLogs(),
        monitoringAPI.getVPNAlerts()
      ]);
      setEmployees(employeesRes.data);
      setLocations(locationsRes.data);
      setLoginLogs(logsRes.data);
      setVpnAlerts(vpnRes.data);
    } catch (error) {
      console.error('Monitoring fetch error:', error);
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      setRefreshing(true);
      const response = await monitoringAPI.getAllLocations();
      setLocations(response.data);
    } catch (error) {
      console.error('Location refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchEmployeeActivity = async (employeeId) => {
    try {
      const response = await monitoringAPI.getEmployeeActivity(employeeId);
      setActivities(response.data);
    } catch (error) {
      console.error('Activity fetch error:', error);
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    setSelectedEmployee(employeeId);
    fetchEmployeeActivity(employeeId);
  };

  const getEmployeeLocation = (employeeId) => {
    return locations.find(l => l.employee_id === employeeId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading monitoring data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monitoring-page">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Active Sessions</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {locations.length}
                </p>
              </div>
              <Monitor className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Online Now</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {employees.filter(e => e.status === 'active').length}
                </p>
              </div>
              <Activity className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 text-sm">VPN Detected</p>
                <p className="text-3xl font-bold text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {vpnAlerts.length}
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Logins Today</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {loginLogs.filter(l => l.timestamp?.includes(new Date().toISOString().split('T')[0])).length}
                </p>
              </div>
              <Shield className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="live" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="live" data-testid="tab-live">Live Monitoring</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Login Logs</TabsTrigger>
          <TabsTrigger value="vpn" data-testid="tab-vpn">VPN Alerts</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>

        {/* Live Monitoring */}
        <TabsContent value="live">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Live Employee Status</CardTitle>
                  <CardDescription>Real-time location updates every 30 seconds</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={fetchLocations}
                  disabled={refreshing}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {employees.map((employee) => {
                  const location = getEmployeeLocation(employee.employee_id);
                  return (
                    <div 
                      key={employee.employee_id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        location?.vpn_detected ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                      }`}
                      data-testid={`employee-status-${employee.employee_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={employee.avatar} />
                            <AvatarFallback className="bg-indigo-100 text-indigo-600">
                              {employee.first_name?.[0]}{employee.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                            location ? 'bg-emerald-500' : 'bg-slate-300'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {employee.first_name} {employee.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{employee.position}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        {location ? (
                          <>
                            <div className="text-right">
                              <div className="flex items-center gap-1 text-sm text-slate-600">
                                <MapPin className="w-4 h-4" />
                                {location.city}, {location.country}
                              </div>
                              <p className="text-xs text-slate-400 font-mono">
                                {location.ip_address}
                              </p>
                            </div>
                            {location.vpn_detected ? (
                              <Badge className="bg-red-100 text-red-700">
                                <WifiOff className="w-3 h-3 mr-1" />
                                VPN
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <Wifi className="w-3 h-3 mr-1" />
                                Direct
                              </Badge>
                            )}
                          </>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500">Offline</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Login Logs */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Login History</CardTitle>
              <CardDescription>Recent authentication events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {loginLogs.slice(0, 20).map((log, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-4 rounded-lg ${
                      log.vpn_detected ? 'bg-red-50' : 'bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        log.vpn_detected ? 'bg-red-100' : 'bg-indigo-100'
                      }`}>
                        <Shield className={`w-5 h-5 ${log.vpn_detected ? 'text-red-600' : 'text-indigo-600'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Employee {log.employee_id}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Globe className="w-3 h-3" />
                          <span className="font-mono">{log.ip_address}</span>
                          <span>•</span>
                          <span>{log.city}, {log.country}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                      {log.vpn_detected && (
                        <Badge className="bg-red-100 text-red-700 mt-1">VPN Detected</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VPN Alerts */}
        <TabsContent value="vpn">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                VPN/Proxy Alerts
              </CardTitle>
              <CardDescription>Suspicious connection attempts detected</CardDescription>
            </CardHeader>
            <CardContent>
              {vpnAlerts.length > 0 ? (
                <div className="space-y-3">
                  {vpnAlerts.map((alert, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-red-900">Employee {alert.employee_id}</p>
                          <div className="flex items-center gap-2 text-sm text-red-600">
                            <span className="font-mono">{alert.ip_address}</span>
                            <span>•</span>
                            <span>{alert.city}, {alert.country}</span>
                            <span>•</span>
                            <span>{alert.isp}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-red-600 text-white">
                          VPN/Proxy
                        </Badge>
                        <p className="text-sm text-red-600 mt-1">
                          {new Date(alert.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Shield className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-emerald-700 mb-2">All Clear</h3>
                  <p className="text-slate-500">No VPN or proxy connections detected</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Activity Monitor</CardTitle>
                  <CardDescription>Screen activity and application usage</CardDescription>
                </div>
                <Select value={selectedEmployee || ''} onValueChange={handleEmployeeSelect}>
                  <SelectTrigger className="w-64" data-testid="employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {activities.length > 0 ? (
                <div className="space-y-3">
                  {activities.map((activity, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          activity.activity_type === 'idle' ? 'bg-amber-100' : 'bg-emerald-100'
                        }`}>
                          {activity.activity_type === 'idle' ? (
                            <Clock className="w-5 h-5 text-amber-600" />
                          ) : (
                            <Monitor className="w-5 h-5 text-emerald-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {activity.application || activity.website || 'Unknown'}
                          </p>
                          <p className="text-sm text-slate-500">
                            {activity.activity_type} • Idle: {activity.idle_time}s
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-500">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Eye className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Select an Employee</h3>
                  <p className="text-slate-500">Choose an employee to view their activity</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Monitoring;
